import { useState, useEffect, useMemo, useDeferredValue, useRef } from 'react';
import { addDoc, collection, serverTimestamp, updateDoc, doc, getDoc, setDoc } from 'firebase/firestore';
import { ORDER_TABLES, canChargeDispatchOrder } from '../../lib/dispatchOrders';
import { db } from '../../firebase';
import ChargeOrderModal from '../ChargeOrderModal';
import IceCreamPOS from '../POS/IceCreamPOS';
import RecipeChoicePOS from '../POS/RecipeChoicePOS';
import { processOrderSale } from '../../lib/saleService';
import { getOpenCashStatus } from '../../lib/cashApi';
import { useSettings } from '../../context/SettingsContext';
import { normalizePrintingSettings } from '../../lib/printing';
import { requiresDirectStock, resolveRequestedSaleQuantity } from '../../lib/productStockPolicy';
import { isProductAvailableToday, resolvePromotionProduct } from '../../lib/promotionRules';

const ITEM_NOTE_SUGGESTIONS = ['Sin cebolla', 'Sin salsa', 'Poco picante', 'Extra queso', 'Sin hielo'];


const WaiterOrderView = ({ onBack, products, currentUser, onSendToKitchen }) => {
  const { settings } = useSettings();
  const printingSettings = useMemo(() => normalizePrintingSettings(settings?.printing), [settings?.printing]);
  const moneyFormatter = useMemo(() => {
    const currency = settings?.currency || 'COP';
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    });
  }, [settings?.currency]);
  const fmtMoney = (value) => {
    try {
      return moneyFormatter.format(Number(value || 0));
    } catch (_) {
      return Number(value || 0).toLocaleString('es-CO', {
        style: 'currency',
        currency: 'COP',
        maximumFractionDigits: 0,
      });
    }
  };

  const calcTotals = (items = []) => {
    const subtotal = (items || []).reduce(
      (s, it) => s + Number(it.price || 0) * Number(it.quantity || 0),
      0
    );
    if (!settings?.taxEnabled) {
      return { subtotal, taxes: 0, total: subtotal };
    }
    const rate = Number(settings?.taxRate || 0) / 100;
    const taxes = subtotal * rate;
    return { subtotal, taxes, total: subtotal + taxes };
  };

  const [currentOrder, setCurrentOrder] = useState({
    id: Date.now(),
    table: '',
    items: [],
    status: 'Pendiente', // Pendiente, En Preparacion, Listo para Cobro, Cobrado
    waiter: currentUser,
    orderTime: new Date().toISOString(),
    notes: ''
  });

  const [searchTerm, setSearchTerm] = useState('');
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const [selectedCategory, setSelectedCategory] = useState('Todas');
  const [activePane, setActivePane] = useState('menu');
  const PAGE_SIZE = 15;
  const [currentPage, setCurrentPage] = useState(1);
  const [openIceCream, setOpenIceCream] = useState(false);
  const [selectedIceProduct, setSelectedIceProduct] = useState(null);
  const [openRecipeChoice, setOpenRecipeChoice] = useState(false);
  const [selectedRecipeProduct, setSelectedRecipeProduct] = useState(null);
  const [notice, setNotice] = useState({ open: false, message: '', type: 'info' });
  const [quickFeedback, setQuickFeedback] = useState('');
  const [processing, setProcessing] = useState(false);

const showNotice = (msg, type = 'info') => setNotice({ open: true, message: msg, type });
const hideNotice = () => setNotice(prev => ({ ...prev, open: false }));
const flashAdded = (name) => setQuickFeedback(`Agregado: ${name}`);

useEffect(() => {
  if (!notice.open || notice.type !== 'success') return undefined;
  const timer = setTimeout(() => hideNotice(), 2200);
  return () => clearTimeout(timer);
}, [notice.open, notice.type]);

useEffect(() => {
  if (!quickFeedback) return undefined;
  const timer = setTimeout(() => setQuickFeedback(''), 1100);
  return () => clearTimeout(timer);
}, [quickFeedback]);
  const [showCharge, setShowCharge] = useState(false);  // <- abre/cierra el modal de cobro
  const [itemNoteDraft, setItemNoteDraft] = useState({ open: false, lineId: null, name: '', value: '' });
  const categoriesScrollRef = useRef(null);
  const categoriesPointerRef = useRef({
    pointerId: null,
    startX: 0,
    startScrollLeft: 0,
    moved: false,
    dragging: false,
  });

  // --- NUEVO: solo productos para vender (excluye insumos) ---
  const sellableProducts = useMemo(() => {
    const list = Array.isArray(products) ? products : [];
    return list
      .filter(p => p?.deleted !== true && (p?.type || '').toLowerCase() !== 'insumo')
      .filter(p => isProductAvailableToday(p))
      .map(p => resolvePromotionProduct(p, list))
      .filter(Boolean);
  }, [products]);

// Mapa id -> stock disponible (desde products)
const stockById = useMemo(() => {
  const m = new Map();
  (products || []).forEach(p => m.set(p.id, Number(p.stock || 0)));
  return m;
}, [products]);


  // --- NUEVO: categorías a partir de los vendibles ---
  const categories = useMemo(() => {
    const set = new Set(['Todas']);
    sellableProducts.forEach(p => {
      const c = (p?.category || p?.categoria || 'Sin categoría');
      set.add(c);
    });
    return Array.from(set);
  }, [sellableProducts]);

  const filteredProducts = useMemo(() => {
    let temp = sellableProducts;

    if (selectedCategory !== 'Todas') {
      temp = temp.filter(p => (p?.category || p?.categoria) === selectedCategory);
    }

    const term = (deferredSearchTerm || '').toLowerCase();
    if (term) {
      temp = temp.filter(p =>
        String(p?.name || '').toLowerCase().includes(term) ||
        String(p?.barcode || '').toLowerCase().includes(term)
      );
    }

    return temp;
  }, [deferredSearchTerm, selectedCategory, sellableProducts]);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE)),
    [filteredProducts.length]
  );
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredProducts.slice(start, start + PAGE_SIZE);
  }, [filteredProducts, currentPage]);

  const orderTotals = useMemo(
    () => calcTotals(currentOrder.items),
    [currentOrder.items, settings?.taxEnabled, settings?.taxRate]
  );




  const buildOrderItemPayload = (item) => {
    if (!item) return null;
  const lineId = item._lineId || crypto.randomUUID?.() || `${item.id}-${Date.now()}`;
  const payload = {
    id: item.id,
    name: item.name,
    price: Number(item.price || 0),
    quantity: Number(item.quantity || 0),
    _lineId: lineId,
    observation: String(item.observation || '').trim(),
  };

    const hasCustomComponents =
      Array.isArray(item.recipeComponents) && item.recipeComponents.length > 0;
    if (hasCustomComponents) {
      payload.recipeComponents = item.recipeComponents
        .map((comp) => ({
          componentId: comp.componentId || comp.insumoId || comp.productId || comp.id,
          name: comp.name || comp.productName || '',
          qtyPerUnit: Number(comp.qtyPerUnit || comp.qty || comp.quantity || 0),
          unit: comp.unit || comp.u || '',
          insumoId: comp.insumoId || comp.componentId || comp.productId || comp.id,
        }))
        .filter((comp) => comp.componentId);
    } else if (isRecipeProduct(item)) {
      const components = getRecipeComponents(item);
      if (components.length > 0) {
        payload.recipeComponents = components;
      }
    }

    return payload;
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory]);

  useEffect(() => {
    setCurrentPage(prev => Math.min(prev, totalPages));
  }, [totalPages]);

// v NUEVO: estado de base (turno) del mesero
const [checkingTurn, setCheckingTurn] = useState(true);
const [hasOpenTurn, setHasOpenTurn] = useState(false);
// Modal "Enviado a Despacho"
const [showDispatched, setShowDispatched] = useState(false);
const [dispatchInfo, setDispatchInfo] = useState({
  orderId: null,
  table: '',
  itemsCount: 0,
  subtotal: 0
});


useEffect(() => {
  let alive = true;
  (async () => {
    if (!currentUser) return;
    setCheckingTurn(true);
    try {
      const t = await getOpenTurn(currentUser);
      if (alive) setHasOpenTurn(!!t);
    } catch (error) {
      console.error('No se pudo validar el turno del mesero', error);
      if (alive) setHasOpenTurn(false);
    } finally {
      if (alive) setCheckingTurn(false);
    }
  })();
  return () => { alive = false; };
}, [currentUser]);


// helper para saber si es receta/compuesto
const isRecipeProduct = (p) => {
  if (!p) return false;
  if (Array.isArray(p?.recipeComponents) && p.recipeComponents.length) return true;
  const hasCfgRecipe =
    Boolean(p?.config?.recipe?.baseItems?.length || p?.config?.baseItems?.length) ||
    Boolean(p?.config?.recipe?.choiceGroups?.length) ||
    Boolean(p?.config?.isIceCream);
  if (hasCfgRecipe) return true;
  return p?.type === 'receta';
};

const getRecipeComponents = (product) => {
  const safeList = (arr) => (Array.isArray(arr) ? arr : []);

  if (product?.config?.isIceCream && product?.config?.baseInsumoId) {
  const baseComponent = {
    componentId: product.config.baseInsumoId,
    insumoId: product.config.baseInsumoId,
    qtyPerUnit: 1,
    name: product.config.baseInsumoName || 'base',
  };
  return [baseComponent];
  }

  const baseItems =
    safeList(product?.config?.recipe?.baseItems).length > 0
      ? product.config.recipe.baseItems
      : safeList(product?.config?.baseItems);

    const recipeFromConfig = safeList(baseItems).map((bi) => {
      const compId = bi.productId || bi.id;
      return {
        componentId: compId,
        insumoId: compId,
        qtyPerUnit: Number(bi.qty || bi.qtyPerUnit || bi.quantity || 1),
        name: bi.name || bi.productName || '',
      };
    });

  if (recipeFromConfig.length > 0) return recipeFromConfig;

    return safeList(product?.recipeComponents).map((comp) => {
      const compId = comp.insumoId || comp.productId || comp.id;
      return {
        componentId: compId,
        insumoId: compId,
        qtyPerUnit: Number(comp.qtyPerUnit || comp.qty || comp.quantity || 1),
        name: comp.name || comp.productName || '',
      };
    });
};

const handleIceCreamConfirmed = (item) => {
  const quantity = Number(item.quantity || 1);

  setCurrentOrder(prevOrder => {
    const nextId = item._lineId || crypto.randomUUID();
    const existing = prevOrder.items.find(it => it.id === item.id && it._lineId === nextId);
    if (existing) {
      return {
        ...prevOrder,
        items: prevOrder.items.map(it =>
          it._lineId === existing._lineId ? { ...it, quantity: Number(it.quantity || 0) + quantity } : it
        )
      };
    }

    return {
      ...prevOrder,
      items: [...prevOrder.items, { ...item, quantity, _lineId: nextId }]
    };
  });
  flashAdded(item.name);
};

const handleAddItemToOrder = (product) => {
  // Si es receta/combo -> NO revises product.stock aquí
  if (isRecipeProduct(product)) {
    if (product?.config?.isIceCream) {
      setSelectedIceProduct(product);
      setOpenIceCream(true);
      return;
    }
    const choiceGroups = Array.isArray(product?.config?.recipe?.choiceGroups)
      ? product.config.recipe.choiceGroups
      : [];
    if (choiceGroups.length > 0) {
      setSelectedRecipeProduct(product);
      setOpenRecipeChoice(true);
      return;
    }
    const components = getRecipeComponents(product);
    if (components.length === 0) {
      alert('La receta no tiene insumos configurados.');
      return;
    }

    let wasAdded = false;
    setCurrentOrder(prevOrder => {
      const existing = prevOrder.items.find(it => it.id === product.id);
      const nextQty = existing ? Number(existing.quantity || 0) + 1 : 1;

      if (existing) {
        wasAdded = true;
        return {
          ...prevOrder,
          items: prevOrder.items.map(it =>
            it.id === product.id
              ? {
                  ...it,
                  quantity: nextQty,
                  recipeComponents: it.recipeComponents?.length ? it.recipeComponents : components,
                }
              : it
          )
        };
      }

      wasAdded = true;
      return {
        ...prevOrder,
        items: [...prevOrder.items, {
          ...product,
          quantity: 1,
          _lineId: crypto.randomUUID(),
          recipeComponents: components,
        }]
      };
    });
    if (wasAdded) flashAdded(product.name);
    return;
  }

  // Producto normal (NO receta): sí validamos stock directo
  const controlsOwnStock = requiresDirectStock(product);
  const available = Number(product?.stock || 0);
  if (controlsOwnStock && available <= 0) {
    alert(`"${product?.name}" no tiene stock disponible.`);
    return;
  }

  let wasAdded = false;
  setCurrentOrder(prevOrder => {
    const existing = prevOrder.items.find(it => it.id === product.id);
    if (existing) {
      const nextQty = Number(existing.quantity || 0) + 1;
      if (controlsOwnStock && nextQty > available) {
        alert(`No puedes pedir más de ${available} de "${product.name}".`);
        return prevOrder;
      }
      wasAdded = true;
      return {
        ...prevOrder,
        items: prevOrder.items.map(it =>
          it.id === product.id ? { ...it, quantity: nextQty } : it
        )
      };
    }

    wasAdded = true;
    return {
      ...prevOrder,
      items: [...prevOrder.items, { ...product, quantity: 1, _lineId: crypto.randomUUID() }]
    };
  });
  if (wasAdded) flashAdded(product.name);
};

  const closeIceCreamModal = () => {
    setOpenIceCream(false);
    setSelectedIceProduct(null);
  };

  const closeRecipeChoiceModal = () => {
    setOpenRecipeChoice(false);
    setSelectedRecipeProduct(null);
  };

  const handleRecipeChoiceConfirmed = (item) => {
    const quantity = Number(item.quantity || 1);

  setCurrentOrder(prevOrder => ({
    ...prevOrder,
    items: [...prevOrder.items, { ...item, quantity, observation: String(item.observation || ''), _lineId: item._lineId || crypto.randomUUID() }]
  }));
  flashAdded(item.name);
};

const openItemObservation = (item) => {
  setItemNoteDraft({
    open: true,
    lineId: item?._lineId || item?.id || null,
    name: item?.name || 'Producto',
    value: String(item?.observation || ''),
  });
};

const closeItemObservation = () => {
  setItemNoteDraft({ open: false, lineId: null, name: '', value: '' });
};

const saveItemObservation = () => {
  if (!itemNoteDraft.lineId) {
    closeItemObservation();
    return;
  }

  const nextValue = String(itemNoteDraft.value || '').trim();
  setCurrentOrder((prevOrder) => ({
    ...prevOrder,
    items: prevOrder.items.map((item) =>
      (item._lineId || item.id) === itemNoteDraft.lineId
        ? { ...item, observation: nextValue }
        : item
    ),
  }));
  closeItemObservation();
};

  const handleRemoveItemFromOrder = (lineId) => {
    setCurrentOrder(prevOrder => ({
      ...prevOrder,
      items: prevOrder.items.filter(item => (item._lineId || item.id) !== lineId)
    }));
  };

const handleQuantityChange = (lineId, newQuantity) => {
  const item = currentOrder.items.find(i => (i._lineId || i.id) === lineId);
  if (!item) return;

  const decision = resolveRequestedSaleQuantity(item, newQuantity, stockById.get(item.id));
  if (!decision.allowed) {
    showNotice(`No hay stock disponible de "${item.name}".`, 'error');
    return;
  }
  if (decision.capped) {
    showNotice(`La cantidad de "${item.name}" se limitó al stock disponible (${decision.available}).`, 'warning');
  }

  setCurrentOrder(prevOrder => ({
    ...prevOrder,
    items: prevOrder.items.map(i =>
      (i._lineId || i.id) === lineId ? { ...i, quantity: decision.quantity } : i
    )
  }));
};

const stepItemQuantity = (lineId, currentQuantity, delta) => {
  const nextQuantity = Math.max(1, Number(currentQuantity || 1) + delta);
  handleQuantityChange(lineId, String(nextQuantity));
};

const handleSendToKitchen = async () => {
  if (currentOrder.items.length === 0) {
    showNotice('El pedido está vacío. Agrega productos antes de enviar a despacho.');
    return;
  }
  if (!currentOrder.table) {
    showNotice('Por favor, asigna una mesa o un nombre de pedido.');
    return;
  }

  const orderToSend = {
    // datos mínimos
    table: currentOrder.table,
    items: currentOrder.items
      .map(buildOrderItemPayload)
      .filter(Boolean),
    status: 'En Preparacion',
    dispatchStatus: 'Nuevo',
    notes: currentOrder.notes || '',
    // trazabilidad del mesero
    waiterUid: currentUser?.uid || null,
    waiterName: currentUser?.username || currentUser?.email || 'Mesero',
    source: 'waiter_order',
    // referencias/fechas
    clientId: currentOrder.id,                // el Date.now() local, útil como referencia
    orderTime: new Date().toISOString(),      // ISO del cliente (visible en ticket)
    createdAt: serverTimestamp(),             // sello confiable del servidor
    kitchenPrintStatus: printingSettings.autoPrintKitchen ? 'pending' : 'disabled',
    kitchenPrintRequestedAt: printingSettings.autoPrintKitchen ? serverTimestamp() : null,
    // totales
    ...calcTotals(currentOrder.items),
  };

  // 1) Guarda en Firestore
  const docRef = await addDoc(collection(db, 'orders'), orderToSend);

  // 2) (si tu app aún usa el callback) avisa "arriba" que se creó
  if (typeof onSendToKitchen === 'function') {
    onSendToKitchen({ ...orderToSend, id: docRef.id });
  }

  // 3) La estacion principal recibe la cola por Firebase y la imprime por localhost.
  // El celular del mesero no se conecta a ninguna IP de la red local.

setDispatchInfo({
  orderId: docRef.id,
  table: currentOrder.table,
  itemsCount: currentOrder.items.reduce((s,i)=>s+Number(i.quantity||0),0),
  subtotal: calcTotals(currentOrder.items).total,
  items: currentOrder.items.map((item) => ({
    name: item.name,
    quantity: Number(item.quantity || 0),
    observation: String(item.observation || '').trim(),
  })),
});
setShowDispatched(true);

  // 4) Reset
  setCurrentOrder({
    id: Date.now(),
    table: '',
    items: [],
    status: 'Pendiente',
    waiter: currentUser,
    orderTime: new Date().toISOString(),
    notes: ''
  });
};

const handleReadyForPayment = async () => {
  if (currentOrder.items.length === 0) {
    showNotice('El pedido está vacío.');
    return;
  }
  if (!currentOrder.table) {
    showNotice('Por favor, asigne una mesa o un nombre de pedido.');
    return;
  }

  const orderToPay = {
    table: currentOrder.table,
    items: currentOrder.items
      .map(buildOrderItemPayload)
      .filter(Boolean),
    status: 'Listo para Cobro',
    notes: currentOrder.notes || '',
    waiterUid: currentUser?.uid || null,
    waiterName: currentUser?.username || currentUser?.email || 'Mesero',
    source: 'waiter_order',
    clientId: currentOrder.id,
    orderTime: new Date().toISOString(),
    createdAt: serverTimestamp(),
    readyForPaymentAt: serverTimestamp(),
    ...calcTotals(currentOrder.items),
  };

  const docRef = await addDoc(collection(db, 'orders'), orderToPay);

  if (typeof onReadyForPayment === 'function') {
    onReadyForPayment({ ...orderToPay, id: docRef.id });
  }

    setCurrentOrder({
    id: Date.now(),
    table: '',
    items: [],
    status: 'Pendiente',
    waiter: currentUser,
    orderTime: new Date().toISOString(),
    notes: ''
  });
};

// Abre el modal de cobro
const handleChargeNow = () => {
  if (!canChargeDispatchOrder(currentUser)) {
    showNotice('No tienes permiso para cobrar pedidos.', 'error');
    return;
  }
  if (currentOrder.items.length === 0) {
    showNotice('El pedido está vacío.');
    return;
  }
  if (!currentOrder.table) {
    showNotice('Selecciona mesa / nombre de pedido.');
    return;
  }

    if (!hasOpenTurn) {                 // v NUEVO
      showNotice('No tienes base asignada. Solicítala al administrador.', 'error');
      return;
    }
  setShowCharge(true);
};

const brandPink = '#ff2ba6';
const brandPinkSoft = '#fff0f8';

const actionButtons = (
  <>
    <button
      onClick={handleSendToKitchen}
      className="min-h-[46px] w-full rounded-2xl px-4 py-3 text-base font-semibold text-white shadow-sm transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-[#ffc1e5] focus:ring-offset-2 sm:min-h-[48px] sm:text-[17px]"
      style={{ backgroundColor: brandPink }}
    >
      Enviar a Despacho
    </button>

    <button
      onClick={handleChargeNow}
      disabled={!hasOpenTurn || !canChargeDispatchOrder(currentUser)}
      className={`min-h-[46px] w-full rounded-2xl px-4 py-3 text-base font-semibold shadow-sm transition sm:min-h-[48px] sm:text-[17px] ${
        !hasOpenTurn ? 'cursor-not-allowed bg-gray-200 text-gray-500' : 'bg-emerald-600 text-white hover:bg-emerald-700'
      }`}
    >
      Cobrar ahora
    </button>

    {!hasOpenTurn && (
      <p className="text-sm text-amber-700 sm:text-base">
        No tienes base asignada. Solicítala al administrador.
      </p>
    )}
  </>
);

const getCashierKey = (u) => u?.email || u?.uid || String(u || '');

async function getOpenTurn(user) {
  if (!user) return null;
  return getOpenCashStatus();
}

async function openTurn(user, monto) {
  const key = getCashierKey(user);
  const ref = await addDoc(collection(db, 'cajas'), {
    user: key,
    fecha: new Date().toISOString().slice(0, 10),
    hora: new Date().toLocaleTimeString(),
    montoApertura: Number(monto) || 0,
    estado: 'abierta',
    openedAt: serverTimestamp(),
  });
  return { id: ref.id };
}

/** Garantiza caja abierta: si no hay, pide base y la abre. */
// Reemplaza ensureTurn actual por este:
async function ensureTurn(user) {
  const turno = await getOpenTurn(user);
  if (!turno) {
    throw new Error('No tienes base asignada. Solicítala al administrador.');
  }
  return turno;
}


// Confirmación del modal: crea order (traza), procesa venta y marca cobrado
const confirmCharge = async ({ paymentMethod, amount1, paymentMethod2, amount2 }) => {
  setProcessing(true);
  let persistedInvoice = null;
  try {
    if (!canChargeDispatchOrder(currentUser)) throw new Error('No tienes permiso para cobrar pedidos.');
// Validación de stock antes de cobrar
for (const it of currentOrder.items) {
  const quantity = Number(it.quantity || 0);
  if (quantity <= 0) continue;

  if (isRecipeProduct(it) || !requiresDirectStock(it)) {
    // Recetas y helados pueden consumirse aunque sus insumos queden negativos.
    continue;
  }

  const available = Number(stockById.get(it.id) || 0);
      if (available < quantity) {
        showNotice(`No hay stock suficiente de "${it.name}". Disponible: ${available}`, 'error');
        return;
      }
}


    // Asegurar caja abierta para este usuario (mesero)
    const turn = await ensureTurn(currentUser);  // <- AQUÍ

    // 1) Doc del pedido (trazabilidad)
    const orderDoc = {
      table: currentOrder.table,
      items: currentOrder.items
        .map(buildOrderItemPayload)
        .filter(Boolean),
      status: 'Procesando Cobro',
      notes: currentOrder.notes || '',
      waiterUid: currentUser?.uid || null,
      waiterName: currentUser?.username || currentUser?.email || 'Mesero',
      source: 'waiter_order',
      clientId: currentOrder.id,
      orderTime: new Date().toISOString(),
      createdAt: serverTimestamp(),
      readyForPaymentAt: serverTimestamp(),
      ...calcTotals(currentOrder.items),
    };
    const directOrderId = `direct_${String(currentOrder.id || Date.now()).replace(/[^A-Za-z0-9_-]/g, '')}_${String(currentUser?.uid || 'user').replace(/[^A-Za-z0-9_-]/g, '')}`.slice(0, 120);
    const orderRef = doc(db, 'orders', directOrderId);
    const previousOrderSnapshot = await getDoc(orderRef);
    if (previousOrderSnapshot.exists()) {
      // Reintento seguro: conserva identidad/createdAt para no romper las reglas.
      await updateDoc(orderRef, {
        status: 'Procesando Cobro',
        readyForPaymentAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } else {
      await setDoc(orderRef, orderDoc);
    }

    // 2) Venta + salidas + consecutivo
    const { invoiceNumber, total, change } = await processOrderSale({
      order: {
        id: orderRef.id,
        table: currentOrder.table,
        items: currentOrder.items,
        waiterUid: currentUser?.uid,
        waiterName: currentUser?.username || currentUser?.email,
      },
      cashier: currentUser,          // el mesero cobra
      paymentMethod,
      amount1,
      paymentMethod2,
      amount2,
      turnId: turn.id,               // <- PASA EL TURNO (base)
    });

    // 3) Confirma el pedido y encola el ticket para el PC principal.
    // En un reintento, no volvemos a encolar un ticket que ya este pendiente,
    // imprimiendo o impreso.
    persistedInvoice = invoiceNumber;
    const latestOrderSnapshot = await getDoc(orderRef);
    const existingSalePrintStatus = latestOrderSnapshot.exists()
      ? latestOrderSnapshot.data()?.salePrintStatus
      : null;
    const alreadyQueuedOrPrinted = ['pending', 'printing', 'printed'].includes(existingSalePrintStatus);
    const salePrintStatus = printingSettings.autoPrintSales
      ? (alreadyQueuedOrPrinted ? existingSalePrintStatus : 'pending')
      : (existingSalePrintStatus || 'disabled');

    await updateDoc(orderRef, {
      status: 'Cobrado',
      paidAt: serverTimestamp(),
      saleInvoice: invoiceNumber,
      cashierUid: currentUser?.uid || null,
      cashierName: currentUser?.username || currentUser?.email || 'Mesero',
      turnId: turn.id,
      paymentMethod: paymentMethod || '',
      amountReceived: Number(amount1 || 0),
      secondPaymentMethod: paymentMethod2 || '',
      secondAmountReceived: Number(amount2 || 0),
      change: Number(change || 0),
      saleTotal: Number(total || 0),
      salePrintStatus,
      ...(printingSettings.autoPrintSales && !alreadyQueuedOrPrinted
        ? { salePrintRequestedAt: serverTimestamp(), salePrintError: null }
        : {}),
    });

    // 4) Reset. La impresion la realiza PrintStationRelay en el PC principal.
    setShowCharge(false);
    setCurrentOrder({
      id: Date.now(), table: '', items: [], status: 'Pendiente',
      waiter: currentUser, orderTime: new Date().toISOString(), notes: ''
    });
  } catch (e) {
    console.error('confirmCharge error:', e);
    try {
      const directOrderId = `direct_${String(currentOrder.id || '').replace(/[^A-Za-z0-9_-]/g, '')}_${String(currentUser?.uid || 'user').replace(/[^A-Za-z0-9_-]/g, '')}`.slice(0, 120);
      if (directOrderId && currentOrder.id) {
        const failedRef = doc(db, 'orders', directOrderId);
        const failedSnap = await getDoc(failedRef);
        if (!persistedInvoice && failedSnap.exists() && failedSnap.data()?.status !== 'Cobrado') {
          await updateDoc(failedRef, {
            status: 'Cobro Fallido',
            paymentFailedAt: serverTimestamp(),
            paymentError: String(e?.message || e).slice(0, 500),
          });
        }
      }
    } catch (traceError) {
      console.error('No se pudo registrar el cobro fallido:', traceError);
    }
    showNotice(persistedInvoice
      ? `La venta ${persistedInvoice} ya se registró, pero no se pudo confirmar el pedido. No solicites otro pago; concilia esta factura. Detalle: ${e?.message || e}`
      : (e?.message || 'No se pudo completar el cobro.'), 'error');
  } finally {
    setProcessing(false);
  }
};


  const currentItemCount = currentOrder.items.reduce(
    (sum, item) => sum + Number(item.quantity || 0),
    0
  );
  const currentTableLabel = currentOrder.table || 'Sin mesa asignada';
  const handleCategoriesWheel = (event) => {
    const el = categoriesScrollRef.current;
    if (!el) return;
    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
    event.preventDefault();
    el.scrollLeft += event.deltaY;
  };
  const handleCategoriesPointerDown = (event) => {
    const el = categoriesScrollRef.current;
    if (!el) return;
    categoriesPointerRef.current = {
      pointerId: event.pointerId,
      dragging: true,
      moved: false,
      startX: event.clientX,
      startScrollLeft: el.scrollLeft,
    };
    el.setPointerCapture?.(event.pointerId);
  };
  const handleCategoriesPointerMove = (event) => {
    const el = categoriesScrollRef.current;
    const drag = categoriesPointerRef.current;
    if (!el || !drag.dragging) return;
    const delta = event.clientX - drag.startX;
    if (!drag.moved && Math.abs(delta) > 6) {
      drag.moved = true;
    }
    if (!drag.moved) return;
    event.preventDefault();
    el.scrollLeft = drag.startScrollLeft - delta;
  };
  const stopCategoriesPointer = (event) => {
    const el = categoriesScrollRef.current;
    if (el && event?.pointerId != null) {
      try {
        el.releasePointerCapture?.(event.pointerId);
      } catch (_) {}
    }
    categoriesPointerRef.current.dragging = false;
    categoriesPointerRef.current.pointerId = null;
    setTimeout(() => {
      categoriesPointerRef.current.moved = false;
    }, 0);
  };
  const handleCategoryClick = (category) => {
    if (categoriesPointerRef.current.moved) return;
    setSelectedCategory(category);
  };

  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-rose-50 p-3 pb-24 sm:p-6 lg:pb-8">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 sm:gap-5">
      <div className="rounded-[28px] border border-slate-200 bg-white/95 p-4 shadow-sm sm:p-6">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-center">
            <button
              onClick={onBack}
              className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-2xl border border-sky-200 px-4 py-3 text-sm font-semibold text-sky-700 transition hover:border-sky-400 hover:text-sky-900 sm:h-11 sm:w-auto sm:shrink-0"
              aria-label="Volver al menú"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Volver al Menú
            </button>
            <div className="min-w-0 flex-1 text-center sm:text-left">
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#ff2ba6] sm:text-xs sm:tracking-[0.28em]">Toma de pedidos</p>
              <h1 className="mt-1 truncate text-[28px] font-bold tracking-tight text-slate-950 sm:text-[34px]">
                {settings?.storeName || 'Meseros'}
              </h1>
              <p className="mt-2 text-sm text-slate-500 sm:text-base">
                {currentUser?.username || currentUser?.email || 'Equipo de salón'}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:min-w-[260px] sm:max-w-[320px]">
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 sm:text-[11px]">Productos</p>
              <p className="mt-1 text-xl font-bold text-slate-900 sm:text-2xl">{filteredProducts.length}</p>
            </div>
            <div className={`rounded-2xl border px-4 py-3 text-left ${hasOpenTurn ? 'border-emerald-200 bg-[#edf8ef]' : 'border-amber-200 bg-[#fff7e7]'}`}>
              <p className={`text-[10px] font-semibold uppercase tracking-[0.18em] sm:text-[11px] ${hasOpenTurn ? 'text-emerald-700' : 'text-amber-700'}`}>Caja</p>
              <p className={`mt-1 text-base font-bold sm:text-xl ${hasOpenTurn ? 'text-emerald-950' : 'text-amber-950'}`}>
                {checkingTurn ? 'Validando...' : hasOpenTurn ? 'Activa' : 'Pendiente'}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-1 flex gap-2 rounded-2xl border border-gray-200 bg-white p-1 shadow-sm lg:hidden">
          <button
            type="button"
            onClick={() => setActivePane('menu')}
            className={`flex-1 rounded-xl px-4 py-2 text-sm font-semibold transition ${
              activePane === 'menu' ? 'bg-blue-600 text-white shadow' : 'text-gray-600'
            }`}
          >
            Menú
          </button>
          <button
            type="button"
            onClick={() => setActivePane('order')}
            className={`flex-1 rounded-xl px-4 py-2 text-sm font-semibold transition ${
              activePane === 'order' ? 'bg-blue-600 text-white shadow' : 'text-gray-600'
            }`}
          >
            Pedido
          </button>
        </div>

{!checkingTurn && !hasOpenTurn && (
  <div className="mb-1 rounded-2xl border border-amber-300 bg-[#fff7e7] px-4 py-3 text-sm text-amber-900">
    <b>No tienes base asignada.</b> Solicítala al administrador para poder cobrar.
    <button
      onClick={async () => {
        setCheckingTurn(true);
        const t = await getOpenTurn(currentUser);
        setHasOpenTurn(!!t);
        setCheckingTurn(false);
      }}
      className="ml-3 rounded-xl border border-amber-300 px-3 py-1.5 font-semibold hover:bg-amber-100"
    >
      Reintentar
    </button>
  </div>
)}

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1.12fr)_minmax(360px,0.88fr)] xl:grid-cols-[minmax(0,1.16fr)_420px]">
        {/* Sección de Productos */}
        <div className={`order-2 min-h-[340px] flex-col rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm sm:p-5 lg:order-1 lg:flex ${activePane === 'menu' ? 'flex' : 'hidden'}`}>
          <div className="border-b border-slate-100 pb-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400 sm:text-xs">Catálogo</p>
                <h2 className="mt-1 text-xl font-bold tracking-tight text-slate-950 sm:text-2xl">Menú de Productos</h2>
                <p className="mt-2 text-sm text-slate-500 sm:text-base">Busca y toca para sumar al pedido.</p>
              </div>
              <div className="hidden rounded-2xl bg-slate-50 px-4 py-3 text-center text-slate-900 sm:block">
                <p className="text-[10px] uppercase tracking-[0.24em] text-slate-400">Página</p>
              <p className="mt-1 text-sm font-semibold sm:text-base">{currentPage} / {totalPages}</p>
              </div>
            </div>
          </div>
          <div className="mt-3">
            <label className="flex min-h-[46px] items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 transition focus-within:border-[#ffc1e5] focus-within:bg-white focus-within:shadow-[0_0_0_3px_rgba(255,43,166,0.08)] sm:min-h-[48px]">
              <svg className="h-5 w-5 shrink-0 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m21 21-4.35-4.35m1.85-5.15a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" />
              </svg>
              <input
                type="text"
                placeholder="Buscar por nombre o código"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-transparent text-base font-medium text-slate-700 outline-none placeholder:text-slate-400 sm:text-[17px]"
              />
            </label>
          </div>
          <div
            ref={categoriesScrollRef}
            onWheel={handleCategoriesWheel}
            onPointerDown={handleCategoriesPointerDown}
            onPointerMove={handleCategoriesPointerMove}
            onPointerUp={stopCategoriesPointer}
            onPointerLeave={stopCategoriesPointer}
            onPointerCancel={stopCategoriesPointer}
            className="mt-4 flex gap-2.5 overflow-x-auto pb-2 pt-1 select-none whitespace-nowrap [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:gap-3"
            style={{ touchAction: 'pan-x' }}
          >
            {categories.map((category) => {
              const active = selectedCategory === category;
              return (
                <button
                  type="button"
                  key={category}
                  onClick={() => handleCategoryClick(category)}
                className={`shrink-0 whitespace-nowrap rounded-full px-3.5 py-2.5 text-sm font-medium transition sm:px-4 sm:py-2.5 ${
                    active
                      ? 'text-white shadow-sm'
                      : 'border border-slate-200 bg-slate-50 text-slate-700 hover:bg-white'
                  }`}
                  style={active ? { backgroundColor: brandPink } : undefined}
                >
                  {category}
                </button>
              );
            })}
          </div>
          <div className="mt-4 space-y-3 pr-0 sm:pr-1">
            {paginatedProducts.length > 0 ? (
              paginatedProducts.map((product) => (
                <button
                  type="button"
                  key={product.id}
                  className="group flex w-full flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-3.5 text-left shadow-sm transition hover:border-[#ffd0eb] hover:shadow-md sm:flex-row sm:items-center sm:gap-3"
                  onClick={() => handleAddItemToOrder(product)}
                >
                  <div className="flex min-w-0 w-full flex-1 items-center gap-3">
                    {product.image ? (
                      <img
                        src={product.image}
                        alt={product.name}
                        loading="lazy"
                        decoding="async"
                        draggable="false"
                        className="h-11 w-11 shrink-0 rounded-xl object-cover sm:h-12 sm:w-12 sm:rounded-2xl"
                      />
                    ) : (
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-400 sm:h-12 sm:w-12 sm:rounded-2xl">
                        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 7h16M7 4h10a1 1 0 011 1v2H6V5a1 1 0 011-1Zm-1 3h12l-1 11a2 2 0 01-2 2H9a2 2 0 01-2-2L6 7Z" />
                        </svg>
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="break-words text-base font-semibold leading-5 text-slate-950 sm:text-[17px] sm:leading-6">{product.name}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-500 sm:mt-1.5 sm:text-sm">
                        <span>{product.category || product.categoria || 'Sin categoría'}</span>
                        {!!product.barcode && <span>· Código: {String(product.barcode)}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex w-full items-center justify-between border-t border-slate-100 pt-2.5 sm:w-auto sm:block sm:border-t-0 sm:pt-0 sm:text-right">
                    <p className="text-lg font-bold text-slate-900 sm:text-xl">{fmtMoney(product.price)}</p>
                    <p className="text-xs font-medium text-slate-400 transition group-hover:text-[#ff2ba6] sm:text-sm">Tocar para agregar</p>
                  </div>
                </button>
              ))
            ) : (
              <div className="mt-8 rounded-[24px] border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-slate-500">
                No se encontraron productos.
              </div>
            )}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 border-t border-slate-100 pt-4 text-sm text-slate-600 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center sm:text-base">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className={`min-h-[44px] rounded-2xl px-4 py-2 font-semibold sm:min-h-[46px] ${
                currentPage === 1
                  ? 'cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400'
                  : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              Anterior
            </button>
            <div className="col-span-2 text-center text-xs font-medium leading-5 text-slate-500 sm:col-span-1 sm:text-sm">
              Página {currentPage} de {totalPages} · {filteredProducts.length} productos · 15 por página
            </div>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className={`min-h-[44px] rounded-2xl px-4 py-2 font-semibold sm:min-h-[46px] ${
                currentPage >= totalPages
                  ? 'cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400'
                  : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              Siguiente
            </button>
          </div>
        </div>

        {/* Sección del Pedido */}
<section className={`order-1 lg:order-2 ${activePane === 'order' ? 'block' : 'hidden lg:block'}`}>
  <div className="flex flex-col gap-4 rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
    <div className="shrink-0 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#ff2ba6] sm:text-xs">Pedido actual</p>
          <h2 className="mt-1 text-xl font-bold tracking-tight text-slate-950 sm:text-[26px]">Mesa y resumen</h2>
          <p className="mt-2 text-sm text-slate-500 sm:text-base">Selecciona la mesa, agrega productos y envíalo cuando esté listo.</p>
        </div>
        <div className="rounded-2xl border border-[#ffc1e5] px-4 py-3 text-right" style={{ backgroundColor: brandPinkSoft }}>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#c2187a] sm:text-xs">Items</p>
          <p className="mt-1 text-xl font-bold text-[#7a134c] sm:text-2xl">{currentItemCount}</p>
        </div>
      </div>

      <div className="grid gap-2.5 sm:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900">
          <p className="text-[10px] uppercase tracking-[0.16em] text-slate-500 sm:text-xs">Mesa / pedido</p>
          <p className="mt-1 truncate text-base font-semibold sm:text-lg">{currentTableLabel}</p>
        </div>
        <div className="rounded-2xl border border-[#ffc1e5] px-4 py-3 text-slate-900" style={{ backgroundColor: brandPinkSoft }}>
          <p className="text-[10px] uppercase tracking-[0.16em] text-[#c2187a] sm:text-xs">Total</p>
          <p className="mt-1 text-xl font-bold sm:text-2xl">{fmtMoney(orderTotals.total)}</p>
        </div>
      </div>

      <div>
        <label className="mb-2 block text-sm font-semibold text-slate-700 sm:text-base">Mesa / Nombre del Pedido</label>
        <select
          value={currentOrder.table}
          onChange={(e) => setCurrentOrder((prev) => ({ ...prev, table: e.target.value }))}
          className="min-h-[46px] w-full rounded-[16px] border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-base font-medium text-slate-800 outline-none transition focus:border-[#ffc1e5] focus:bg-white focus:shadow-[0_0_0_3px_rgba(255,43,166,0.08)] sm:min-h-[48px] sm:rounded-[18px] sm:px-4 sm:py-3 sm:text-[17px]"
          required
        >
          <option value="">Seleccionar mesa...</option>
          {ORDER_TABLES.map(table => (
            <option key={table} value={table}>{table}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 gap-3 border-t border-slate-100 pt-4">
        {actionButtons}
      </div>
    </div>

    <div className="min-h-[220px] pr-0 sm:pr-1">
      <div className="space-y-3">
        {currentOrder.items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500 sm:text-base">
            <svg className="mx-auto mb-3 h-12 w-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            Agrega ítems al pedido.
          </div>
        ) : (
          currentOrder.items.map((item, index) => (
            <div
              key={item._lineId || `${item.id}-${index}`}
              className="rounded-2xl border border-slate-200 bg-slate-50 p-3 shadow-sm"
            >
              <div className="flex flex-col gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  {item.image && (
                    <img
                      src={item.image}
                      alt={item.name}
                      loading="lazy"
                      decoding="async"
                      draggable="false"
                      className="h-10 w-10 shrink-0 rounded-xl object-cover sm:h-11 sm:w-11 sm:rounded-2xl"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="break-words text-base font-semibold leading-5 text-slate-950 sm:text-[17px]">{item.name}</p>
                    <p className="mt-1 text-sm text-slate-500 sm:text-base">
                      {fmtMoney(Number(item.price || 0))} c/u
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openItemObservation(item)}
                      className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                        item.observation
                          ? 'border-amber-300 bg-amber-100 text-amber-900 hover:bg-amber-200'
                          : 'border-blue-200 bg-white text-blue-700 hover:bg-blue-50'
                      }`}
                    >
                      {item.observation ? 'Editar obs.' : 'Obs.'}
                    </button>
                    <button
                      onClick={() => handleRemoveItemFromOrder(item._lineId || item.id)}
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-red-200 bg-red-50 text-red-600 transition hover:bg-red-100 hover:text-red-700 sm:h-11 sm:w-11"
                      aria-label={`Eliminar ${item.name}`}
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>

                {item.observation ? (
                  <button
                    type="button"
                    onClick={() => openItemObservation(item)}
                    className="block w-full rounded-xl bg-amber-50 px-3 py-2 text-left text-sm text-amber-900"
                  >
                    <span className="font-semibold">Observación:</span> {item.observation}
                  </button>
                ) : null}

                <div className="grid grid-cols-[minmax(0,1fr)_110px] gap-2">
                  <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5">
                    <label className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400 sm:text-xs">
                      Cantidad
                    </label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => stepItemQuantity(item._lineId || item.id, item.quantity, -1)}
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-lg font-semibold text-slate-700 transition hover:bg-slate-100"
                        aria-label={`Disminuir cantidad de ${item.name}`}
                      >
                        -
                      </button>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => handleQuantityChange(item._lineId || item.id, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        className="min-w-0 flex-1 border-0 bg-transparent p-0 text-center text-lg font-semibold text-slate-900 outline-none sm:text-xl"
                      />
                      <button
                        type="button"
                        onClick={() => stepItemQuantity(item._lineId || item.id, item.quantity, 1)}
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-lg font-semibold text-slate-700 transition hover:bg-slate-100"
                        aria-label={`Aumentar cantidad de ${item.name}`}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3 text-right text-slate-900">
                    <p className="text-[10px] uppercase tracking-[0.16em] text-slate-500 sm:text-xs">Total</p>
                    <p className="mt-2 text-base font-bold leading-5 sm:text-lg">
                      {fmtMoney(Number(item.price || 0) * Number(item.quantity || 0))}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>

    <div className="shrink-0">
      <label className="mb-2 block text-sm font-semibold text-slate-700 sm:text-base">Notas para cocina</label>
      <textarea
        value={currentOrder.notes}
        onChange={(e) => setCurrentOrder(prev => ({ ...prev, notes: e.target.value }))}
        rows="2"
        placeholder="Ej: sin cebolla, extra picante..."
        className="w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-base text-slate-700 outline-none transition focus:border-[#ffc1e5] focus:bg-white sm:py-3.5 sm:text-[17px]"
      />
    </div>

    <div className="mt-auto flex flex-col space-y-3 lg:hidden">
      {actionButtons}
    </div>
  </div>
</section>
      </div>
      </div>
      </div>
      <ChargeOrderModal
        open={showCharge}
        order={{ table: currentOrder.table, items: currentOrder.items, waiterUid: currentUser?.uid, waiterName: currentUser?.username||currentUser?.email }}
        currentUser={currentUser}
        totalOverride={calcTotals(currentOrder.items).total}
        onClose={() => setShowCharge(false)}
        onConfirm={confirmCharge}
        loading={processing}
      />

      <IceCreamPOS
        open={openIceCream}
        product={selectedIceProduct}
        products={products}
        onClose={closeIceCreamModal}
        onAddToCart={(item) => {
          handleIceCreamConfirmed({ ...item, quantity: Number(item.quantity || 1), _lineId: crypto.randomUUID() });
        }}
      />

      <RecipeChoicePOS
        open={openRecipeChoice}
        product={selectedRecipeProduct}
        products={products}
        onClose={closeRecipeChoiceModal}
        onAddToCart={(item) => {
          handleRecipeChoiceConfirmed({
            ...item,
            quantity: Number(item.quantity || 1),
            _lineId: crypto.randomUUID(),
          });
        }}
      />

      {itemNoteDraft.open && (
        <div className="fixed inset-0 z-[140] flex items-end justify-center bg-slate-900/50 p-3 sm:items-center">
          <div className="w-full max-w-md rounded-3xl bg-white p-5 shadow-2xl">
            <div className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-blue-500">Observación por producto</p>
              <h3 className="mt-1 text-xl font-extrabold text-slate-900">{itemNoteDraft.name}</h3>
              <p className="mt-1 text-sm text-slate-500">Solo se imprime si escribes algo.</p>
            </div>

            <div className="mb-4 flex flex-wrap gap-2">
              {ITEM_NOTE_SUGGESTIONS.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => setItemNoteDraft((prev) => ({ ...prev, value: suggestion }))}
                  className="rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                >
                  {suggestion}
                </button>
              ))}
            </div>

            <textarea
              value={itemNoteDraft.value}
              onChange={(e) => setItemNoteDraft((prev) => ({ ...prev, value: e.target.value }))}
              rows="4"
              placeholder="Escribe la observación para este producto"
              className="w-full resize-none rounded-2xl border border-slate-300 px-4 py-3 text-base focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            />

            <div className="mt-4 flex gap-3">
              <button
                type="button"
                onClick={closeItemObservation}
                className="flex-1 rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => setItemNoteDraft((prev) => ({ ...prev, value: '' }))}
                className="rounded-2xl border border-rose-200 px-4 py-3 text-sm font-semibold text-rose-700 transition hover:bg-rose-50"
              >
                Quitar
              </button>
              <button
                type="button"
                onClick={saveItemObservation}
                className="flex-1 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-700"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {notice.open && (() => {
        const panelClass = notice.type === 'success'
          ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
          : 'bg-white border-gray-200 text-gray-900';
        return (
          <div
            className="fixed inset-0 z-[130] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
            aria-live="assertive"
          >
            <div className={`rounded-2xl shadow-2xl border ${panelClass} p-6 w-full max-w-sm text-center space-y-4`}>
              <div className="text-lg font-semibold">Atención</div>
              <p className="text-sm">{notice.message}</p>
              <button
                onClick={hideNotice}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg shadow hover:bg-emerald-700 transition"
              >
                Aceptar
              </button>
            </div>
          </div>
        );
      })()}

      {quickFeedback && (
        <div className="pointer-events-none fixed left-1/2 top-4 z-[135] -translate-x-1/2">
          <div className="rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white shadow-lg">
            {quickFeedback}
          </div>
        </div>
      )}
      </div>

{/* Modal: Pedido enviado a despacho */}
{showDispatched && (
  <div
    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm"
    onClick={() => setShowDispatched(false)}
    role="dialog"
    aria-modal="true"
  >
    <div
      className="mx-3 w-full max-w-lg rounded-[28px] border border-gray-200 bg-white shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 border-b px-4 py-4 sm:px-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
            <svg className="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7 12a5 5 0 1010 0 5 5 0 00-10 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">¡Pedido enviado a Despacho!</h3>
            <p className="text-sm text-gray-500">Cocina ya lo recibió correctamente.</p>
          </div>
        </div>
        <button
          className="p-2 rounded-lg hover:bg-gray-100"
          onClick={() => setShowDispatched(false)}
          aria-label="Cerrar"
        >
          <svg className="h-5 w-5 text-gray-500" viewBox="0 0 24 24" stroke="currentColor" fill="none">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      {/* Body */}
      <div className="space-y-4 px-4 py-5 sm:px-6">
        <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <div className="rounded-lg border p-3 bg-gray-50">
            <p className="text-gray-500">Mesa / Pedido</p>
            <p className="font-semibold text-gray-900">{dispatchInfo.table || '—'}</p>
          </div>
          <div className="rounded-lg border p-3 bg-gray-50">
            <p className="text-gray-500">N.º Pedido</p>
            <p className="font-semibold text-gray-900 truncate">{dispatchInfo.orderId}</p>
          </div>
          <div className="rounded-lg border p-3 bg-gray-50">
            <p className="text-gray-500">Cant. ítems</p>
            <p className="font-semibold text-gray-900">{dispatchInfo.itemsCount}</p>
          </div>
          <div className="rounded-lg border p-3 bg-gray-50">
            <p className="text-gray-500">Subtotal</p>
            <p className="font-semibold text-gray-900">{fmtMoney(dispatchInfo.subtotal)}</p>
          </div>
        </div>

<div className="flex items-start gap-2 rounded-2xl border border-blue-200 bg-blue-50 p-3">
  <svg className="h-5 w-5 mt-0.5 text-blue-600" viewBox="0 0 24 24" stroke="currentColor" fill="none">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M12 18.5a6.5 6.5 0 110-13 6.5 6.5 0 010 13z"/>
  </svg>
  <p className="text-sm text-blue-900">
    Para cobrar, usa el botón <b>Cobrar ahora</b> de abajo (se habilita sólo si tienes base asignada).
  </p>
</div>
      </div>

      {/* Footer */}
<div className="flex flex-col gap-3 px-4 pb-6 sm:px-6 md:flex-row md:justify-end md:gap-4">
  <button
    onClick={async () => {
      try {
        await updateDoc(doc(db, 'orders', dispatchInfo.orderId), {
          kitchenPrintStatus: 'pending',
          kitchenPrintRequestedAt: serverTimestamp(),
          kitchenPrintError: null,
        });
        showNotice('Reimpresion enviada a la estacion principal.', 'success');
      } catch (error) {
        console.error('No se pudo solicitar la reimpresion:', error);
        showNotice('No se pudo solicitar la reimpresion.', 'error');
      }
    }}
    className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-700"
  >
    Reimprimir cocina
  </button>

  <button
    onClick={() => {
      setShowDispatched(false);
      // ya dejaste el formulario listo para nuevo pedido
    }}
    className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow"
  >
    Nuevo pedido
  </button>

  <button
    onClick={() => setShowDispatched(false)}
    className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-700"
  >
    Cerrar
  </button>
</div>
    </div>
  </div>
)}

    </div>
  </>
  );
};

export default WaiterOrderView;
